
import { toast as sonnerToast } from "sonner";

export type ToastProps = {
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success" | "warning";
  duration?: number;
  priority?: "low" | "normal" | "high";
  action?: {
    label: string;
    onClick: () => void;
  };
};

// Queue to manage notifications and prevent overwhelming the user
class NotificationQueue {
  private queue: ToastProps[] = [];
  private isProcessing = false;
  private activeCount = 0;
  private maxActive = 2; // Maximum number of simultaneous toasts

  add(toastProps: ToastProps) {
    // Filter out low priority notifications if queue is getting full
    if (this.queue.length > 3 && toastProps.priority === "low") {
      return;
    }

    // High priority notifications go to front of queue
    if (toastProps.priority === "high") {
      this.queue.unshift(toastProps);
    } else {
      this.queue.push(toastProps);
    }

    this.processQueue();
  }

  private async processQueue() {
    if (this.isProcessing || this.activeCount >= this.maxActive || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const toastProps = this.queue.shift()!;
    
    // Wait if we're at max capacity
    while (this.activeCount >= this.maxActive) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    this.showToast(toastProps);
    this.isProcessing = false;

    // Continue processing queue
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 300);
    }
  }

  private showToast(toastProps: ToastProps) {
    this.activeCount++;
    
    const defaultDuration = this.getDurationByType(toastProps.variant, toastProps.priority);
    const duration = toastProps.duration || defaultDuration;

    const toastOptions: any = {
      description: toastProps.description,
      duration,
      className: this.getToastClassName(toastProps.variant),
      onDismiss: () => {
        this.activeCount = Math.max(0, this.activeCount - 1);
      },
      onAutoClose: () => {
        this.activeCount = Math.max(0, this.activeCount - 1);
      },
    };

    if (toastProps.action) {
      toastOptions.action = {
        label: toastProps.action.label,
        onClick: toastProps.action.onClick,
      };
    }

    sonnerToast(toastProps.title, toastOptions);
  }

  private getDurationByType(variant?: string, priority?: string): number {
    if (priority === "high" || variant === "destructive") {
      return 8000; // Longer for important messages
    }
    if (priority === "low") {
      return 3000; // Shorter for low priority
    }
    return 5000; // Default
  }

  private getToastClassName(variant?: string): string {
    switch (variant) {
      case "destructive":
        return "bg-destructive text-destructive-foreground border-destructive";
      case "success":
        return "bg-green-100 border-l-4 border-green-500 text-green-800";
      case "warning":
        return "bg-amber-100 border-l-4 border-amber-500 text-amber-800";
      default:
        return "";
    }
  }
}

const notificationQueue = new NotificationQueue();

// Enhanced toast function with smart queueing
export function toast({
  title,
  description,
  variant = "default",
  duration,
  priority = "normal",
  action,
}: ToastProps) {
  // Auto-assign priority based on variant if not specified
  if (priority === "normal") {
    if (variant === "destructive") {
      priority = "high";
    } else if (variant === "success" && title?.toLowerCase().includes("connect")) {
      priority = "high"; // Connection successes are important
    } else if (title?.toLowerCase().includes("error") || title?.toLowerCase().includes("failed")) {
      priority = "high";
    } else if (variant === "success") {
      priority = "low"; // Most success messages can be low priority
    }
  }

  notificationQueue.add({
    title,
    description,
    variant,
    duration,
    priority,
    action,
  });
}

export function useToast() {
  return { toast };
}
